import { Fragment, type ReactNode } from 'react'
import isString from 'lodash/isString'
import { ArrowDownRight, ArrowUpRight, Equal, Lock } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { childOrderEventIdsOf, parentIsResolved } from '@klear/schema/document-review-step'
import { ApprovalRow } from '@/app/document-review/approval-row'
import {
  notedDoubts,
  type ApprovalItem,
  type DoubtItem,
  type FieldItem,
  type SupplierItem
} from '@/app/document-review/approval-items'
import { readContribution } from '@/app/document-review/contribution-arithmetic'
import { extractionValueText } from '@/app/document-review/extraction-receipt'
import { filedRecordMade, joinsAsDuplicate } from '@/app/document-review/filed-records'
import { parentShapeText, proposalShapeText, supplierShapeText } from '@/app/document-review/proposal-shape'
import { proposalFieldLabel, recordCardTitle } from '@/app/document-review/record-card-specs'
import { PaymentLink, RecordDisplayLink, RecordLink, ReviewLink, TextWithLink } from '@/app/document-review/record-link'
import { doubtKindLabel, recordLabel } from '@/app/document-review/review-labels'
import { formatUnsafeMoney } from '@/app/document-review/unsafe-money'
import type { DocumentReviewProposalView } from '@/app/document-review/use-document-review'

export type ProposalCardProps = {
  proposal: DocumentReviewProposalView
  proposals: readonly DocumentReviewProposalView[]
  items: readonly ApprovalItem[]
  readOnly: boolean
  isConfirmed: (key: string) => boolean
  issueFor: (key: string) => string | null
  editingKey: string | null
  editorFor: (item: FieldItem | SupplierItem) => ReactNode
  onToggle: (key: string) => void
  onEdit: (item: FieldItem | SupplierItem) => void
}

function headlineTitle(proposal: DocumentReviewProposalView, whileDeciding: boolean): ReactNode {
  const record = proposal.record
  if (record === null) {
    return proposal.kind === 'trader_document' ? recordCardTitle(proposal.kind) : 'Question for the reviewer'
  }
  if (record.kind === 'attach') {
    return (
      <RecordLink
        recordIdDisplay={proposal.recordIdDisplay}
        orderEventId={record.orderEventId}
        deleted={proposal.recordDeleted}
        whileDeciding={whileDeciding}
      />
    )
  }
  if (record.kind === 'attach_payment') {
    return (
      <PaymentLink
        recordIdDisplay={proposal.recordIdDisplay}
        paymentId={record.paymentId}
        deleted={proposal.recordDeleted}
      />
    )
  }
  const title = recordCardTitle(proposal.kind)
  const number = record.fields.number
  return number === null ? title : `${title} ${number}`
}

function parentContent(proposal: DocumentReviewProposalView, shown: string | null, whileDeciding: boolean): ReactNode {
  const record = proposal.record
  const linked = proposal.parentRecordDisplay
  if (shown === null || linked === null || record?.kind !== 'create' || record.parentOrderEventId === null) {
    return undefined
  }
  return (
    <TextWithLink
      text={shown}
      linked={linked}
      link={
        <RecordLink recordIdDisplay={linked} orderEventId={record.parentOrderEventId} whileDeciding={whileDeciding} />
      }
    />
  )
}

function childrenText(proposal: DocumentReviewProposalView): string {
  const children = childOrderEventIdsOf(proposal.record)
  return children.map((id, index) => recordLabel(proposal.childRecordDisplays[index] ?? null, id)).join(', ')
}

function childrenContent(proposal: DocumentReviewProposalView, whileDeciding: boolean): ReactNode {
  const children = childOrderEventIdsOf(proposal.record)
  return children.map((orderEventId, index) => (
    <Fragment key={orderEventId}>
      {index > 0 && ', '}
      <RecordLink
        recordIdDisplay={proposal.childRecordDisplays[index] ?? null}
        orderEventId={orderEventId}
        whileDeciding={whileDeciding}
      />
    </Fragment>
  ))
}

function updatesRecord(item: FieldItem): boolean {
  const record = item.proposal.record
  return record?.kind === 'attach' && record.updates !== undefined && item.field in record.updates
}

function heldValue(item: FieldItem): string | null {
  if (!updatesRecord(item)) return null
  const replaced: Partial<Record<string, string | null>> = item.proposal.replacedValues
  const held = replaced[item.field] ?? null
  if (held === null || held === item.value) return null
  return item.field === 'amount' ? (formatUnsafeMoney(held) ?? held) : held
}

function replacedValue(item: FieldItem): string | null {
  if (item.proposal.recordFinalization !== null || joinsAsDuplicate(item.proposal)) return null
  return heldValue(item)
}

const OFFER_FINALIZATIONS: Record<string, { label: string; why: string }> = {
  funded: { label: 'Funded offer', why: 'has a funded offer' },
  completed: { label: 'Completed offer', why: 'has a completed offer' }
}

function FinalizedRecordState({
  finalization
}: {
  finalization: NonNullable<DocumentReviewProposalView['recordFinalization']>
}) {
  const offer = finalization.reason === 'offer' ? OFFER_FINALIZATIONS[finalization.offerState] : undefined
  const reason = finalization.reason === 'approved' ? 'Approved' : (offer?.label ?? 'Offer')
  const why = finalization.reason === 'approved' ? 'is approved' : (offer?.why ?? 'has an offer')
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={-1} className="text-muted-foreground flex shrink-0 cursor-help items-center gap-1.5 text-xs">
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              <span className="text-foreground font-medium">{reason}</span> · keeps its values
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          This record {why}, so filing attaches the document and leaves its amount, dates and other values as they are.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function keptValue(item: FieldItem): string | null {
  return item.proposal.recordFinalization === null ? null : heldValue(item)
}

function fieldContent(
  item: FieldItem,
  row: { shown: string; empty: boolean; kept: string | null },
  whileDeciding: boolean
): ReactNode {
  if (row.kept !== null) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-3">
        <span className="inline-flex items-center gap-1">
          <Lock className="text-muted-foreground h-3 w-3 shrink-0" aria-label="Kept" />
          {row.kept}
        </span>
        <span className="text-muted-foreground text-xs font-normal">
          {item.value === null ? 'page clears it' : `page ${row.shown}`}
        </span>
      </span>
    )
  }
  if (item.field === 'externalParentReference' && !row.empty) {
    return parentReferenceContent(item.proposal, row.shown, whileDeciding)
  }
  if (item.amountKind !== undefined) {
    return readContribution({ amount: item.value, amountKind: item.amountKind })?.sentence
  }
  return undefined
}

function fieldShown(item: FieldItem): { shown: string; empty: boolean; original: string | null } {
  const replaced = replacedValue(item)
  if (item.value === null) {
    return { shown: updatesRecord(item) ? 'Cleared' : 'Left empty', empty: true, original: replaced }
  }
  const shown = item.field === 'amount' ? (formatUnsafeMoney(item.value) ?? item.value) : item.value
  const read = item.extraction?.value
  const misread = isString(read) && read !== item.value && read !== shown ? extractionValueText(read) : null
  return { shown, empty: false, original: replaced ?? misread }
}

function FigureChoice({ proposal }: { proposal: DocumentReviewProposalView }) {
  const record = proposal.record
  if (record === null || record.kind !== 'attach') return null
  const reading = readContribution(record.contribution)
  if (reading === null) {
    return <p className="mt-1 text-xs">This document brings no figure of its own to record {record.orderEventId}.</p>
  }
  const Icon = { sets: Equal, adds: ArrowUpRight, reduces: ArrowDownRight }[reading.direction]
  const tone = reading.direction === 'reduces' ? 'text-amber-600 dark:text-amber-500' : 'text-foreground'
  return (
    <div className={`mt-1 flex items-center gap-1.5 text-xs font-medium ${tone}`}>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {reading.sentence}.
    </div>
  )
}

function waitingReview(proposal: DocumentReviewProposalView): string | null {
  return parentIsResolved(proposal.record) ? null : proposal.parentReviewId
}

function parentReferenceContent(
  proposal: DocumentReviewProposalView,
  shown: string,
  whileDeciding: boolean
): ReactNode {
  const record = proposal.record
  const link = parentLink(proposal, whileDeciding)
  if (link !== null) {
    return (
      <>
        {shown}
        <span className="text-muted-foreground font-normal"> → </span>
        {link}
      </>
    )
  }
  if (record?.kind !== 'attach') return undefined
  return (
    <>
      {shown}
      <span className="text-muted-foreground font-normal">
        {' '}
        · no parent on {recordLabel(proposal.recordIdDisplay, record.orderEventId)}
      </span>
    </>
  )
}

function parentLink(proposal: DocumentReviewProposalView, whileDeciding: boolean): ReactNode {
  const parent = proposal.parentRecordDisplay
  if (parent !== null) return <RecordDisplayLink recordIdDisplay={parent} whileDeciding={whileDeciding} />
  const reviewId = waitingReview(proposal)
  return reviewId === null ? null : <ReviewLink processingId={reviewId} whileDeciding={whileDeciding} />
}

function rowDoubts(item: FieldItem): FieldItem['doubts'] {
  if (item.field !== 'externalParentReference') return item.doubts
  const explained = parentIsResolved(item.proposal.record) || waitingReview(item.proposal) !== null
  return explained ? item.doubts.filter((doubt) => doubt.kind !== 'pending_parent') : item.doubts
}

function DoubtDetail({ item }: { item: DoubtItem }) {
  return (
    <>
      {item.doubt.kind === 'figure_choice' && <FigureChoice proposal={item.proposal} />}
      {item.doubt.proposedAnswer !== null && (
        <p className="mt-1 text-xs">
          <span className="text-muted-foreground">Proposed answer: </span>
          <span className="font-medium">{item.doubt.proposedAnswer}</span>
        </p>
      )}
    </>
  )
}

export function ProposalCard({
  proposal,
  proposals,
  items,
  readOnly,
  isConfirmed,
  issueFor,
  editingKey,
  editorFor,
  onToggle,
  onEdit
}: ProposalCardProps) {
  const made = filedRecordMade(proposal)
  const noted = notedDoubts(proposal).filter((doubt) => doubt.kind !== 'finalized_record' || made)
  const whileDeciding = !readOnly

  return (
    <section className="bg-card shrink-0 rounded-lg border">
      <header className="bg-card sticky top-0 z-10 flex items-baseline justify-between gap-3 rounded-t-lg border-b px-3 py-2">
        <div className="min-w-0">
          <span className="text-muted-foreground text-xs">
            Proposal {proposal.ordinal + 1} · {proposalShapeText(proposal)}
          </span>
          <h3 className="truncate text-sm font-medium">{headlineTitle(proposal, whileDeciding)}</h3>
        </div>
        {proposal.recordFinalization !== null && <FinalizedRecordState finalization={proposal.recordFinalization} />}
      </header>

      <div className="divide-y py-1">
        {items.map((item) => {
          switch (item.type) {
            case 'field': {
              const { shown, empty, original } = fieldShown(item)
              const kept = keptValue(item)
              return (
                <ApprovalRow
                  key={item.key}
                  approvalKey={item.key}
                  label={proposalFieldLabel(proposal, item.field)}
                  shown={kept ?? shown}
                  shownContent={fieldContent(item, { shown, empty, kept }, whileDeciding)}
                  empty={empty && kept === null}
                  required={item.required}
                  original={original}
                  extraction={item.extraction}
                  doubts={rowDoubts(item)}
                  confirmed={isConfirmed(item.key)}
                  needsApproval={item.needsApproval}
                  readOnly={readOnly || made}
                  editable={item.editable}
                  editing={editingKey === item.key}
                  editor={editingKey === item.key ? editorFor(item) : undefined}
                  issue={issueFor(item.key)}
                  onToggle={() => onToggle(item.key)}
                  onEdit={() => onEdit(item)}
                />
              )
            }
            case 'supplier':
              return (
                <ApprovalRow
                  key={item.key}
                  approvalKey={item.key}
                  label="Supplier"
                  shown={supplierShapeText(proposal) ?? 'Pick a supplier'}
                  empty={supplierShapeText(proposal) === null}
                  confirmed={isConfirmed(item.key)}
                  needsApproval={item.needsApproval}
                  readOnly={readOnly || made}
                  editable
                  editing={editingKey === item.key}
                  editor={editingKey === item.key ? editorFor(item) : undefined}
                  issue={issueFor(item.key)}
                  onToggle={() => onToggle(item.key)}
                  onEdit={() => onEdit(item)}
                />
              )
            case 'parent': {
              const shown = parentShapeText(proposal, proposals)
              return (
                <ApprovalRow
                  key={item.key}
                  approvalKey={item.key}
                  label="Under"
                  shown={shown ?? ''}
                  shownContent={parentContent(proposal, shown, whileDeciding)}
                  confirmed={isConfirmed(item.key)}
                  needsApproval={item.needsApproval}
                  readOnly={readOnly || made}
                  onToggle={() => onToggle(item.key)}
                />
              )
            }
            case 'children':
              return (
                <ApprovalRow
                  key={item.key}
                  approvalKey={item.key}
                  label="Takes"
                  shown={childrenText(proposal)}
                  shownContent={childrenContent(proposal, whileDeciding)}
                  confirmed={isConfirmed(item.key)}
                  needsApproval={item.needsApproval}
                  readOnly={readOnly || made}
                  onToggle={() => onToggle(item.key)}
                />
              )
            case 'joins': {
              const record = proposal.record
              const target = record !== null && record.kind === 'attach' ? record.orderEventId : null
              return (
                <ApprovalRow
                  key={item.key}
                  approvalKey={item.key}
                  label={
                    record !== null && record.kind === 'attach' && record.fileDocument === false ? 'Updates' : 'Joins'
                  }
                  shown={target === null ? '' : recordLabel(proposal.recordIdDisplay, target)}
                  shownContent={
                    target === null ? undefined : (
                      <RecordLink
                        recordIdDisplay={proposal.recordIdDisplay}
                        orderEventId={target}
                        deleted={proposal.recordDeleted}
                        whileDeciding={whileDeciding}
                      />
                    )
                  }
                  confirmed={isConfirmed(item.key)}
                  needsApproval={item.needsApproval}
                  readOnly={readOnly || made}
                  onToggle={() => onToggle(item.key)}
                />
              )
            }
            case 'doubt':
              return (
                <ApprovalRow
                  key={item.key}
                  approvalKey={item.key}
                  label={doubtKindLabel(item.doubt.kind)}
                  shown={item.doubt.message}
                  detail={<DoubtDetail item={item} />}
                  confirmed={isConfirmed(item.key)}
                  needsApproval={item.needsApproval}
                  readOnly={readOnly || made}
                  confirmLabel="Accept"
                  onToggle={() => onToggle(item.key)}
                />
              )
            default:
              return null
          }
        })}
      </div>

      {noted.length > 0 && (
        <details className="border-t">
          <summary className="text-muted-foreground cursor-pointer px-3 py-1.5 text-xs" tabIndex={-1}>
            {noted.length === 1 ? '1 check the run recorded' : `${noted.length} checks the run recorded`}
          </summary>
          <ul className="flex flex-col gap-1 px-3 pt-1 pb-2">
            {noted.map((doubt, index) => (
              <li key={`${doubt.kind}-${index}`} className="text-muted-foreground text-xs">
                <span className="text-foreground font-medium">{doubtKindLabel(doubt.kind)}.</span> {doubt.message}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
